import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';

class PorlanTeaserApp {
  constructor() {
    this.isMobile = window.innerWidth < 768;
    this.isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
    // True touch device — used to gate cursor binding and to enable touch handlers
    this.isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    // Honour user motion preference — disables the heavy ripple + drift loops
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Listen for runtime changes (user toggles in OS settings while page is open)
    try {
      window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', (e) => {
        this.prefersReducedMotion = e.matches;
      });
    } catch (_) { /* Safari < 14 */ }

    this.mouse = new THREE.Vector2(0, 0);
    this.targetMouse = new THREE.Vector2(0, 0);
    this.clock = new THREE.Clock();

    this.darkColors = {
      particles: 0x0055aa,
      lines: new THREE.Color('#00BFFF'),
      core: 0x00BFFF,
      skillFill: 'transparent',
      skillShadow: 'rgba(0, 191, 255, 0.6)',
      skillText: 'rgba(255, 255, 255, 0.85)',
      skillAccent: '#00BFFF',
      clear: 0x020306
    };

    this.lightColors = {
      particles: 0xff4400,
      lines: new THREE.Color('#cc3300'),
      core: 0xff4400,
      skillFill: 'transparent',
      skillShadow: 'rgba(255, 68, 0, 0.5)',
      skillText: 'rgba(30, 30, 40, 0.9)',
      skillAccent: '#ff4400',
      clear: 0xF0F2F5
    };

    this.skillsList = ['AI', 'DATA', '3D', 'DESIGN', 'JavaScript', 'CREATIVITY', 'UX/UI', 'WEBGL', 'Three.js'];
    this.skillObjects = [];

    this.particlesCount = 0;

    this.initPreloader();
    this.initWebGL();
    this.initUI();
    this.bindEvents();

    this.animate = this.animate.bind(this);
    requestAnimationFrame(this.animate);
  }

  // --- 1. PRELOADER & SPLIT-CURTAIN SEQUENCE ---
  initPreloader() {
    this.bootContainer = document.getElementById('awwwards-loader');
    this.progressLine = document.getElementById('loader-progress');
    this.loaderName = document.getElementById('loader-name');
    this.loaderWelcome = document.getElementById('loader-welcome');
    this.loaderLineContainer = document.querySelector('.loader-line-container');

    if (!this.bootContainer || !this.progressLine || !this.loaderWelcome) return;

    // --- Polyglot words ---
    const words = ['WELCOME', 'BIENVENIDO', 'TERVETULOA', 'ようこそ'];
    const glyphs = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#%&*!?';
    const isLatin = (ch) => /[A-Za-z0-9@#%&*!? ]/.test(ch);

    // --- Timing ---
    const WORD_DECODE_TIME = 600;   // Time each word takes to fully decode
    const WORD_HOLD_TIME = 700;     // Hold visible before dissolving
    const WORD_DISSOLVE_TIME = 300; // Dissolve out duration
    const CHAR_SCRAMBLE_RATE = 40;
    const BAR_START = 800;
    const NAME_START = 1200;

    const WORD_CYCLE_TIME = WORD_DECODE_TIME + WORD_HOLD_TIME + WORD_DISSOLVE_TIME;
    // Bar must fill exactly when last word's hold ends (no dissolve for last word)
    const ACTUAL_END = (words.length - 1) * WORD_CYCLE_TIME + WORD_DECODE_TIME + WORD_HOLD_TIME;
    const BAR_DURATION = ACTUAL_END - BAR_START;

    // --- Build char spans for a word ---
    const buildWord = (word) => {
      this.loaderWelcome.innerHTML = '';
      return word.split('').map(char => {
        const span = document.createElement('span');
        span.className = 'char';
        const canScramble = isLatin(char) && char !== ' ';
        span.textContent = char === ' ' ? '\u00A0' : (canScramble ? glyphs[Math.floor(Math.random() * glyphs.length)] : char);
        span.style.opacity = '0';
        this.loaderWelcome.appendChild(span);
        return { el: span, target: char, canScramble, revealed: false, settled: false, lastScramble: 0 };
      });
    };

    // --- Initial state ---
    if (typeof gsap !== 'undefined') {
      gsap.set(this.loaderLineContainer, { opacity: 0 });
      gsap.set(this.loaderName, { opacity: 0 });
      // Elegant entrance for welcome text
      gsap.set(this.loaderWelcome, { opacity: 0, scale: 0.85, filter: 'blur(8px)' });
      gsap.to(this.loaderWelcome, {
        opacity: 1, scale: 1, filter: 'blur(0px)',
        duration: 0.8, ease: 'power2.out', delay: 0.15
      });
    }

    let currentWordIndex = 0;
    let chars = buildWord(words[0]);
    let wordStartTime = 0;
    let barShown = false;
    let nameShown = false;
    const t0 = performance.now();

    const tick = (now) => {
      const t = now - t0;
      const wordT = t - wordStartTime; // Time within current word cycle

      // --- Word decode phase ---
      if (wordT < WORD_DECODE_TIME) {
        const stagger = WORD_DECODE_TIME / (chars.length + 1);
        for (let i = 0; i < chars.length; i++) {
          const c = chars[i];
          const charTime = (i + 1) * stagger;

          // Reveal char
          if (!c.revealed && wordT >= charTime * 0.3) {
            c.el.style.opacity = '1';
            c.revealed = true;
          }

          // Settle char
          if (!c.settled && wordT >= charTime) {
            c.el.textContent = c.target === ' ' ? '\u00A0' : c.target;
            c.settled = true;
          } else if (c.revealed && !c.settled && c.canScramble && wordT - c.lastScramble >= CHAR_SCRAMBLE_RATE) {
            c.el.textContent = glyphs[Math.floor(Math.random() * glyphs.length)];
            c.lastScramble = wordT;
          }
        }
      }

      // --- Word hold phase (all chars settled, just hold) ---
      // Nothing to do, chars are visible

      // --- Word dissolve phase (skip for last word — revealSite handles it) ---
      const isLastWord = currentWordIndex >= words.length - 1;
      if (!isLastWord && wordT >= WORD_DECODE_TIME + WORD_HOLD_TIME && wordT < WORD_CYCLE_TIME) {
        const dissolveProgress = (wordT - WORD_DECODE_TIME - WORD_HOLD_TIME) / WORD_DISSOLVE_TIME;
        this.loaderWelcome.style.opacity = 1 - dissolveProgress;
      }

      // --- Next word ---
      if (!isLastWord && wordT >= WORD_CYCLE_TIME) {
        currentWordIndex++;
        this.loaderWelcome.style.opacity = '1';
        chars = buildWord(words[currentWordIndex]);
        wordStartTime = t;
      }

      // --- Bar fade-in ---
      if (!barShown && t >= BAR_START && typeof gsap !== 'undefined') {
        barShown = true;
        gsap.to(this.loaderLineContainer, { opacity: 1, duration: 0.8, ease: 'power2.out' });
      }

      // --- Bar fill ---
      if (t >= BAR_START) {
        const progress = Math.min(((t - BAR_START) / BAR_DURATION) * 100, 100);
        this.progressLine.style.width = `${progress}%`;
      }

      // --- Name fade-in ---
      if (!nameShown && t >= NAME_START && typeof gsap !== 'undefined') {
        nameShown = true;
        gsap.to(this.loaderName, { opacity: 1, duration: 1.0, ease: 'power2.out' });
      }

      // --- Done: last word settled + bar full → immediately begin exit ---
      if (isLastWord && wordT >= WORD_DECODE_TIME + WORD_HOLD_TIME) {
        this.progressLine.style.width = '100%';
        setTimeout(() => this.revealSite(), 300);
        return;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  revealSite() {
    if (!this.bootContainer || typeof gsap === 'undefined') return;

    // --- Capture name position BEFORE any layout changes ---
    const nameEl = this.loaderName;
    const nameTarget = document.getElementById('name-target');
    let nameRect = null;
    if (nameEl) {
      nameRect = nameEl.getBoundingClientRect();
    }

    const tl = gsap.timeline();

    // 1. Welcome text blurs away + name fades
    tl.to(this.loaderWelcome, {
      duration: 0.8,
      opacity: 0,
      y: -40,
      filter: 'blur(12px)',
      ease: 'power3.in'
    })
      .to(this.loaderName, {
        duration: 0.5,
        opacity: 0,
        ease: 'power2.in'
      }, '<0.2')
      // 2. Progress line compresses
      .to(this.loaderLineContainer, {
        duration: 0.5,
        scaleX: 0,
        opacity: 0,
        transformOrigin: 'center center',
        ease: 'power2.in'
      }, '<')
      // 3. Split curtains
      .to('.top-curtain', {
        duration: 1.0,
        yPercent: -100,
        ease: 'expo.inOut'
      }, '+=0.05')
      .to('.bottom-curtain', {
        duration: 1.0,
        yPercent: 100,
        ease: 'expo.inOut'
      }, '<')
      // 4. Fade in ocean
      .to('#digital-background', {
        duration: 1.2,
        opacity: 1,
        ease: 'power2.out'
      }, '-=0.5')
      // 5. Cleanup + manual FLIP + site animations
      .add(() => {
        // Hide loader
        this.bootContainer.style.display = 'none';

        // Make all gsap-reveal elements visible
        gsap.set('.gsap-reveal', { visibility: 'visible' });

        // --- Manual FLIP: reparent name and animate from old position ---
        if (nameEl && nameTarget && nameRect) {
          // Reparent into subtitle
          nameEl.classList.remove('loader-name');
          nameEl.classList.add('subtitle-name');
          nameEl.style.opacity = '1';
          nameTarget.appendChild(nameEl);

          // Get new position
          const newRect = nameEl.getBoundingClientRect();

          // Calculate delta
          const dx = nameRect.left + nameRect.width / 2 - (newRect.left + newRect.width / 2);
          const dy = nameRect.top + nameRect.height / 2 - (newRect.top + newRect.height / 2);
          const sx = nameRect.width / newRect.width;
          const sy = nameRect.height / newRect.height;

          // Set to old position and animate to new
          gsap.fromTo(nameEl, {
            x: dx,
            y: dy,
            scaleX: sx,
            scaleY: sy,
            opacity: 0.5
          }, {
            x: 0,
            y: 0,
            scaleX: 1,
            scaleY: 1,
            opacity: 1,
            duration: 1.2,
            ease: 'power3.inOut',
            onComplete: () => {
              // Fade in role text
              const roleEl = document.getElementById('subtitle-role');
              if (roleEl) {
                gsap.to(roleEl, { opacity: 1, duration: 0.6, ease: 'power2.out' });
              }
            }
          });
        } else {
          // Fallback: just set the name text directly
          if (nameTarget) {
            nameTarget.textContent = 'Eduardo Porlan';
          }
          const roleEl = document.getElementById('subtitle-role');
          if (roleEl) {
            gsap.to(roleEl, { opacity: 1, duration: 0.6, ease: 'power2.out', delay: 0.3 });
          }
        }

        // Trigger remaining site animations
        this.triggerGSAPAnimations();
      });
  }

  // --- 2. WEBGL SCENE SETUP ---
  initWebGL() {
    const container = document.getElementById('digital-background');

    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    this.camera.position.z = 150;

    // Try to create the renderer — if WebGL fails (very old device, blocked, headless),
    // fall back to a tasteful static gradient so the page still loads cleanly.
    try {
      this.renderer = new THREE.WebGLRenderer({
        alpha: true,
        antialias: !this.isMobile, // No MSAA on mobile — too expensive and barely visible
        powerPreference: this.isMobile ? "low-power" : "high-performance",
        // Fail loudly so the catch can route us to the fallback
        failIfMajorPerformanceCaveat: false
      });
      // Cap pixel ratio more aggressively on mobile for battery + thermals
      const maxDPR = this.isMobile ? 1.5 : 2;
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, maxDPR));
      this.renderer.setSize(window.innerWidth, window.innerHeight);
      this.renderer.setClearColor(this.darkColors.clear, 1);
      container.appendChild(this.renderer.domElement);
    } catch (err) {
      console.warn('WebGL unavailable — falling back to static background.', err);
      this.webglFailed = true;
      container.style.background = 'radial-gradient(ellipse at 50% 60%, rgba(0,191,255,0.12) 0%, transparent 60%), #020306';
      container.style.opacity = '1';
      // Still need raycaster placeholder so animate() doesn't crash
      this.raycaster = new THREE.Raycaster();
      return;
    }

    // Post-Processing (Bloom) — disabled on mobile and when reduced-motion is on,
    // since the composer's extra passes are the most expensive thing we do.
    this.renderScene = new RenderPass(this.scene, this.camera);
    this.useBloom = !this.isMobile && !this.prefersReducedMotion;

    if (this.useBloom) {
      this.bloomPass = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 1.0, 0.4, 0.85);
      this.bloomPass.threshold = 0.2;
      this.bloomPass.strength = 0.4;
      this.bloomPass.radius = 0.5;

      this.composer = new EffectComposer(this.renderer);
      this.composer.addPass(this.renderScene);
      this.composer.addPass(this.bloomPass);
    }

    // V4 Haptic Interaction prep
    this.raycaster = new THREE.Raycaster();

    this.createDataOcean();
    this.createCelestialSkills(false); // Init dark mode celestial skills

    // V5 Optimization: Cache Title Letter physical coordinates to avoid DOM Layout Thrashing
    this.cachedLetters = [];
    setTimeout(() => {
      this.cacheLetterPositions();
    }, 500); // Give DOM time to layout
  }

  cacheLetterPositions() {
    const titleEl = document.getElementById('main-title');
    if (!titleEl || this.isMobile) return;

    this.cachedLetters = [];
    const letters = titleEl.querySelectorAll('.letter');
    letters.forEach(letter => {
      const rect = letter.getBoundingClientRect();
      this.cachedLetters.push({
        el: letter,
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2
      });
    });
  }

  createCircleTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 32;
    canvas.height = 32;
    const ctx = canvas.getContext('2d');

    // Transparent background
    ctx.clearRect(0, 0, 32, 32);

    // Draw a filled white circle with soft edges
    const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(0.6, 'rgba(255, 255, 255, 1)');
    gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');

    ctx.beginPath();
    ctx.arc(16, 16, 16, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = gradient;
    ctx.fill();

    const texture = new THREE.CanvasTexture(canvas);
    texture.premultiplyAlpha = false;
    return texture;
  }

  createDataOcean() {
    // Vast Topographical Ocean using PlaneGeometry
    // Reduced segments significantly to optimize CPU Math.sin/cos loop performance
    const width = 800;
    const depth = 800;
    const segmentsX = this.isMobile ? 80 : 160;
    const segmentsY = this.isMobile ? 80 : 160;

    const geometry = new THREE.PlaneGeometry(width, depth, segmentsX, segmentsY);

    // Rotate to lie flat like an ocean floor
    geometry.rotateX(-Math.PI / 2);

    // Save base positions for animation reference
    const positions = geometry.attributes.position.array;
    this.baseZ = new Float32Array(positions.length / 3);
    for (let i = 0; i < positions.length; i += 3) {
      this.baseZ[i / 3] = positions[i + 2];
    }

    // Custom ShaderMaterial for GPU-native round particles
    // gl_PointCoord in fragment shader guarantees circles — no texture needed
    this.particlesMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uColor: { value: new THREE.Color(this.darkColors.particles) },
        uSize: { value: this.isMobile ? 3.0 : 4.0 },
        uOpacity: { value: 0.8 },
        uPixelRatio: { value: this.renderer.getPixelRatio() }
      },
      vertexShader: `
        uniform float uSize;
        uniform float uPixelRatio;
        void main() {
          vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = uSize * uPixelRatio * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        uniform float uOpacity;
        void main() {
          // gl_PointCoord goes from 0,0 to 1,1 across the point quad
          // Discard fragments outside a circle of radius 0.5
          float dist = length(gl_PointCoord - vec2(0.5));
          if (dist > 0.5) discard;
          // Soft edge for anti-aliasing
          float alpha = 1.0 - smoothstep(0.35, 0.5, dist);
          gl_FragColor = vec4(uColor, alpha * uOpacity);
        }
      `,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      depthTest: true
    });

    this.particles = new THREE.Points(geometry, this.particlesMaterial);

    // Position the ocean mostly below the camera so it doesn't eclipse the text
    this.particles.position.y = -140;

    this.scene.add(this.particles);
  }

  createSkillText(text, colors) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    // Cinematic italic mono — instantly different from the bold geometric title
    const fontSize = 72;
    const padding = 200; // Massive padding so shadow never touches canvas edge

    ctx.font = `200 ${fontSize}px 'Space Grotesk', sans-serif`;
    ctx.letterSpacing = '8px';
    const metrics = ctx.measureText(text);
    canvas.width = metrics.width + padding;
    canvas.height = fontSize + padding;

    // Clear to fully transparent black
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Re-set font after canvas resize
    ctx.font = `200 ${fontSize}px 'Space Grotesk', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const cx = canvas.width / 2;
    const cy = canvas.height / 2;

    // Glow pass — soft halo behind text
    ctx.shadowColor = colors.skillShadow;
    ctx.shadowBlur = 40;
    ctx.fillStyle = colors.skillText;
    ctx.fillText(text, cx, cy);

    // Crisp core pass — no shadow, sharp text
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.fillText(text, cx, cy);

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.premultiplyAlpha = false;
    texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    texture.minFilter = THREE.LinearFilter;

    const spriteMaterial = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      alphaTest: 0.02, // Hard-clip any near-zero alpha — kills the rectangle
      opacity: 1.0,
      depthTest: false,
      blending: THREE.NormalBlending,
      sizeAttenuation: true
    });
    const sprite = new THREE.Sprite(spriteMaterial);

    const aspect = canvas.width / canvas.height;
    const baseHeight = 14;
    sprite.scale.set(baseHeight * aspect, baseHeight, 1.0);
    return sprite;
  }

  createCelestialSkills(isLightMode) {
    const wasActive = this.skillsActive;
    const prevOpacity = this.globalSkillOpacity ? this.globalSkillOpacity.value : 0;

    this.skillObjects.forEach(obj => {
      if (obj.material.map) obj.material.map.dispose();
      if (obj.material) obj.material.dispose();
      this.scene.remove(obj);
    });
    this.skillObjects.length = 0;

    const colors = isLightMode ? this.lightColors : this.darkColors;

    // Free-orbit: each skill gets a random position + velocity vector
    // They live in an enormous space and can drift beyond any boundary
    const spawnRange = this.isMobile ? 80 : 180;
    const depthRange = this.isMobile ? 40 : 80;

    this.skillsList.forEach((txt, index) => {
      const sprite = this.createSkillText(txt, colors);

      // Random spawn across the entire 3D space
      const x = (Math.random() - 0.5) * spawnRange * 2;
      const y = (Math.random() - 0.5) * spawnRange * 0.8 + 20;
      const z = -(Math.random() * depthRange + 10);

      sprite.position.set(x, y, z);
      sprite.material.opacity = wasActive ? prevOpacity * 0.5 : 0;

      // Each skill gets its own unique velocity for free orbit
      const vx = (Math.random() - 0.5) * 0.15;
      const vy = (Math.random() - 0.5) * 0.08;
      const vz = (Math.random() - 0.5) * 0.05;

      sprite.userData = {
        velocity: new THREE.Vector3(vx, vy, vz),
        // Soft boundary — when skills drift too far, gently curve back
        spawnRange: spawnRange,
        depthRange: depthRange,
        timeOffset: Math.random() * 100,
        baseScale: sprite.scale.clone()
      };

      this.skillObjects.push(sprite);
      this.scene.add(sprite);
    });

    this.skillsActive = wasActive;
    this.globalSkillOpacity = { value: wasActive ? prevOpacity : 0 };
  }

  // --- 3. UI, EVENTS & ANIMATIONS ---
  initUI() {
    this.initCursor();
    this.splitTitleText();
    this.initWeather();

    // Subscribe block: hover-activated via CSS, no click needed

    // Audio Logic
    this.soundButton = document.getElementById('sound-toggle');
    this.ambientAudio = document.getElementById('ambient-audio');

    if (localStorage.getItem('soundState') === 'on') {
      this.soundButton.classList.add('sound-on');
      this.soundButton.classList.remove('sound-off');
    } else {
      this.soundButton.classList.add('sound-off');
      this.soundButton.classList.remove('sound-on');
    }

    // Theme Logic
    this.themeToggle = document.getElementById('theme-toggle');
    let currentTheme = localStorage.getItem('theme') || 'dark';
    this.applyTheme(currentTheme);
  }

  initCursor() {
    this.cursor = document.querySelector('.custom-cursor');
    if (this.cursor && !this.isMobile) {
      document.querySelectorAll('a, button, .weather-group, .subscribe-trigger').forEach(el => {
        el.addEventListener('mouseenter', () => this.cursor.classList.add('hover'));
        el.addEventListener('mouseleave', () => this.cursor.classList.remove('hover'));
      });
    }
  }

  splitTitleText() {
    const titleEl = document.getElementById('main-title');
    if (!titleEl) return;
    const originalText = titleEl.textContent;
    titleEl.innerHTML = '';
    const words = originalText.trim().split(' ');

    words.forEach((word) => {
      const wordWrap = document.createElement('div');
      wordWrap.style.display = 'inline-block';
      // V5.3 Fix: Removed wordWrap.style.overflow = 'hidden' to prevent physics clipping
      wordWrap.style.verticalAlign = 'top';

      const wordSpan = document.createElement('span');
      wordSpan.className = 'word';
      wordSpan.style.display = 'inline-block';

      word.split('').forEach((char) => {
        const span = document.createElement('span');
        span.className = 'letter';
        span.innerHTML = char;
        if (char === '.') span.classList.add('dot');
        wordSpan.appendChild(span);
      });

      wordWrap.appendChild(wordSpan);
      titleEl.appendChild(wordWrap);
      titleEl.appendChild(document.createTextNode(' '));
    });
  }

  triggerGSAPAnimations() {
    if (typeof gsap === 'undefined') return;

    // V5.5 Trigger Skills WebGL Loop (Wait for master opacity to drive them)
    this.skillsActive = true;
    this.globalSkillOpacity = { value: 0 };

    const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

    // 1. Skills Bloom Outward (Global scale/opacity driver handled in Animate Loop over time)
    tl.to(this.globalSkillOpacity, { value: 1, duration: 1.2, ease: 'power2.out' })
      // 2. Title Blurs/Scales into existence
      .from('.premium-title .word', { autoAlpha: 0, filter: 'blur(10px)', scale: 0.9, duration: 0.9, stagger: 0.08, ease: 'power3.out' }, '-=0.8')
      // Note: subtitle name handled by FLIP in revealSite()
      // 3. UI Panels Frame the Art Last
      .from('.hud-border.top', { scaleX: 0, transformOrigin: 'left center', duration: 1.0, ease: 'expo.inOut' }, '-=0.4')
      .from('.hud-border.bottom', { scaleX: 0, transformOrigin: 'right center', duration: 1.0, ease: 'expo.inOut' }, '<')
      .from('.hud-border.left', { scaleY: 0, transformOrigin: 'top center', duration: 1.0, ease: 'expo.inOut' }, '<0.1')
      .from('.hud-border.right', { scaleY: 0, transformOrigin: 'bottom center', duration: 1.0, ease: 'expo.inOut' }, '<')
      .from('.hud-corner', { scale: 0, rotation: 45, duration: 0.5, stagger: 0.08, autoAlpha: 0 }, '-=0.4')
      .from('.header-ui.gsap-reveal', { autoAlpha: 0, y: -20, duration: 0.8 }, '-=0.4')
      // V5.6 Fix: Fade the entire .links container at once so the glass box doesn't load empty before text
      .from('.links.gsap-reveal', { autoAlpha: 0, y: 15, duration: 0.7 }, '-=0.5')
      .from('.controls-ui.gsap-reveal', { autoAlpha: 0, x: 20, duration: 0.7 }, '-=0.5')
      .from('.footer-ui.gsap-reveal', { autoAlpha: 0, y: 20, duration: 0.7 }, '-=0.5');
  }

  async initWeather() {
    const update = async () => {
      try {
        const [lRes, hRes] = await Promise.all([
          fetch('https://api.open-meteo.com/v1/forecast?latitude=37.67&longitude=-1.70&current_weather=true').catch(() => null),
          fetch('https://api.open-meteo.com/v1/forecast?latitude=60.17&longitude=24.94&current_weather=true').catch(() => null)
        ]);

        if (lRes && lRes.ok) {
          const data = await lRes.json();
          document.getElementById('lorca-temp').textContent = Math.round(data.current_weather.temperature) + '°';
        }
        if (hRes && hRes.ok) {
          const data = await hRes.json();
          document.getElementById('helsinki-temp').textContent = Math.round(data.current_weather.temperature) + '°';
        }
      } catch (err) {
        console.warn('Weather telemetry unavailable.');
      }
      document.getElementById('lorca-time').textContent = new Date().toLocaleString('en-GB', { timeZone: 'Europe/Madrid', hour: '2-digit', minute: '2-digit' });
      document.getElementById('helsinki-time').textContent = new Date().toLocaleString('en-GB', { timeZone: 'Europe/Helsinki', hour: '2-digit', minute: '2-digit' });
    };

    update();
    setInterval(update, 60000);

    // Weather cards — hover on desktop, tap-toggle on touch.
    const cards = [
      { trigger: 'lorca-weather', card: 'lorca-card' },
      { trigger: 'helsinki-weather', card: 'helsinki-card' }
    ];
    const allTriggers = [];
    const allCards = [];

    cards.forEach(c => {
      const trigger = document.getElementById(c.trigger);
      const card = document.getElementById(c.card);
      if (!trigger || !card) return;
      allTriggers.push(trigger);
      allCards.push({ trigger, card });

      // Desktop / fine-pointer: hover-driven
      if (!this.isTouch) {
        trigger.addEventListener('mouseenter', () => {
          card.classList.add('is-visible');
          trigger.setAttribute('aria-expanded', 'true');
        });
        trigger.addEventListener('mouseleave', () => {
          card.classList.remove('is-visible');
          trigger.setAttribute('aria-expanded', 'false');
        });
      }

      // Touch / keyboard: tap or Enter toggles the card.
      // Desktop with mouse already gets hover open/close — skip click here so we don't
      // immediately close what the hover just opened.
      if (this.isTouch) {
        trigger.addEventListener('click', (e) => {
          e.stopPropagation();
          const isOpen = card.classList.contains('is-visible');

          // Close all other cards first (only one open at a time)
          allCards.forEach(({ card: c2, trigger: t2 }) => {
            c2.classList.remove('is-visible');
            t2.setAttribute('aria-expanded', 'false');
          });

          if (!isOpen) {
            card.classList.add('is-visible');
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      }

      // Keyboard: Escape closes
      trigger.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          card.classList.remove('is-visible');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    });

    // Tap-outside closes any open card (touch UX)
    document.addEventListener('click', (e) => {
      const clickedTrigger = allTriggers.some(t => t.contains(e.target));
      const clickedCard = allCards.some(({ card }) => card.contains(e.target));
      if (!clickedTrigger && !clickedCard) {
        allCards.forEach(({ card, trigger }) => {
          card.classList.remove('is-visible');
          trigger.setAttribute('aria-expanded', 'false');
        });
      }
    });
  }

  // --- AUDIO CONTROLS ---
  fadeOutAudio() {
    clearInterval(this.audioFadeInterval);
    let vol = this.ambientAudio.volume;
    this.audioFadeInterval = setInterval(() => {
      if (vol > 0.05) {
        vol -= 0.05;
        this.ambientAudio.volume = Math.max(0, vol);
      } else {
        clearInterval(this.audioFadeInterval);
        this.ambientAudio.pause();
        this.ambientAudio.volume = 0;
      }
    }, 50);
  }

  fadeInAudio() {
    clearInterval(this.audioFadeInterval);
    this.ambientAudio.volume = 0;
    this.ambientAudio.play().catch(e => console.error("Audio play failed:", e));
    let vol = 0;
    this.audioFadeInterval = setInterval(() => {
      if (vol < 0.95) {
        vol += 0.05;
        this.ambientAudio.volume = Math.min(1, vol);
      } else {
        this.ambientAudio.volume = 1;
        clearInterval(this.audioFadeInterval);
      }
    }, 50);
  }

  toggleSound() {
    const isSoundOn = localStorage.getItem('soundState') === 'on';
    if (isSoundOn) {
      this.fadeOutAudio();
      this.soundButton.classList.remove('sound-on');
      this.soundButton.classList.add('sound-off');
      this.soundButton.setAttribute('aria-pressed', 'false');
      localStorage.setItem('soundState', 'off');
    } else {
      this.fadeInAudio();
      this.soundButton.classList.remove('sound-off');
      this.soundButton.classList.add('sound-on');
      this.soundButton.setAttribute('aria-pressed', 'true');
      localStorage.setItem('soundState', 'on');
    }
  }

  // --- THEME ---
  applyTheme(theme) {
    const isLight = theme === 'light';
    document.body.classList.toggle('light-mode', isLight);

    // Reflect theme on icon buttons for screen readers
    const themeBtn = document.getElementById('theme-toggle');
    if (themeBtn) themeBtn.setAttribute('aria-pressed', String(isLight));

    // If WebGL didn't load, just toggle the body class and exit cleanly
    if (this.webglFailed || !this.renderer) return;

    // Let WebGL render the exact background color to prevent UnrealBloomPass black-crush
    const clearColor = isLight ? this.lightColors.clear : this.darkColors.clear;
    this.renderer.setClearColor(clearColor, 1);
    this.scene.background = new THREE.Color(clearColor);

    // Adjust blending and colors based on theme
    const blendMode = isLight ? THREE.NormalBlending : THREE.AdditiveBlending;

    if (this.particlesMaterial) {
      this.particlesMaterial.uniforms.uColor.value.setHex(isLight ? this.lightColors.particles : this.darkColors.particles);
      this.particlesMaterial.blending = blendMode;
      this.particlesMaterial.uniforms.uOpacity.value = isLight ? 0.35 : 0.8;
      this.particlesMaterial.needsUpdate = true;
    }

    // Soft bloom in dark mode, almost none in light mode (only if bloom is active)
    if (this.bloomPass) {
      this.bloomPass.strength = isLight ? 0.0 : 0.4;
    }

    // Regenerate Skills with correct theme colors
    this.createCelestialSkills(isLight);
  }

  bindEvents() {
    // Real debounce: only fire 200ms after the user STOPS resizing.
    // Important on mobile where the URL bar show/hide fires resize repeatedly.
    let resizeId = null;
    window.addEventListener('resize', () => {
      if (resizeId) clearTimeout(resizeId);
      resizeId = setTimeout(() => {
        this.isMobile = window.innerWidth < 768;
        this.isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
        if (this.camera && this.renderer) {
          this.camera.aspect = window.innerWidth / window.innerHeight;
          this.camera.updateProjectionMatrix();
          this.renderer.setSize(window.innerWidth, window.innerHeight);
          if (this.composer) this.composer.setSize(window.innerWidth, window.innerHeight);
        }
        this.cacheLetterPositions();
        resizeId = null;
      }, 200);
    });

    // Pointer-style update: shared by mouse and touch
    const updatePointer = (clientX, clientY) => {
      // Cursor follow (fine pointer only)
      if (this.cursor && !this.isMobile && !this.isTouch) {
        this.cursor.style.transform = `translate3d(calc(${clientX}px - 50%), calc(${clientY}px - 50%), 0)`;
      }

      // Normalised coords for 3D interactions
      this.targetMouse.x = (clientX / window.innerWidth) * 2 - 1;
      this.targetMouse.y = -(clientY / window.innerHeight) * 2 + 1;
      this.mouse.x = this.targetMouse.x;
      this.mouse.y = this.targetMouse.y;

      // GSAP letter physics — only on fine pointer + when GSAP is loaded + no reduced-motion
      if (typeof gsap !== 'undefined'
          && this.cachedLetters.length > 0
          && !this.isMobile
          && !this.isTouch
          && !this.prefersReducedMotion) {
        this.cachedLetters.forEach(cached => {
          const dx = cached.centerX - clientX;
          const dy = cached.centerY - clientY;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 150) {
            const force = (150 - dist) / 150;
            gsap.to(cached.el, {
              x: (dx / dist) * 20 * force,
              y: (dy / dist) * 20 * force,
              rotationZ: (dx / dist) * 10 * force,
              ease: 'elastic.out(1, 0.3)',
              duration: 0.8,
              overwrite: 'auto'
            });
          } else {
            gsap.to(cached.el, {
              x: 0,
              y: 0,
              rotationZ: 0,
              ease: 'power2.out',
              duration: 0.4,
              overwrite: 'auto'
            });
          }
        });
      }
    };

    document.addEventListener('mousemove', e => updatePointer(e.clientX, e.clientY));

    // Touch: pipe drag/move into the same pipeline so the ocean ripples on phone too.
    // passive:true means we don't preventDefault — page scroll never gets blocked.
    document.addEventListener('touchmove', (e) => {
      if (e.touches && e.touches[0]) {
        updatePointer(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: true });

    document.addEventListener('touchstart', (e) => {
      if (e.touches && e.touches[0]) {
        updatePointer(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: true });

    if (this.soundButton) {
      this.soundButton.addEventListener('click', () => this.toggleSound());
    }

    if (this.themeToggle) {
      this.themeToggle.addEventListener('click', () => {
        const isLight = document.body.classList.contains('light-mode');
        const newTheme = isLight ? 'dark' : 'light';
        this.applyTheme(newTheme);
        localStorage.setItem('theme', newTheme);
      });
    }

    // Re-cache letters when fonts finish loading (Space Grotesk arrives async from Google)
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.cacheLetterPositions());
    }
  }

  // --- MAIN LOOP ---
  animate() {
    requestAnimationFrame(this.animate);

    // No WebGL? Nothing to render — skip the loop body entirely.
    if (this.webglFailed || !this.renderer) return;

    const delta = this.clock.getDelta();
    const elapsedTime = this.clock.getElapsedTime();

    // Smooth mouse interpolation
    this.mouse.x += (this.targetMouse.x - this.mouse.x) * 0.05;
    this.mouse.y += (this.targetMouse.y - this.mouse.y) * 0.05;

    // Cinematic Camera Drift — frozen for reduced-motion users
    if (!this.prefersReducedMotion) {
      this.camera.position.x += (this.mouse.x * 30 - this.camera.position.x) * 0.05;
      this.camera.position.y += (-this.mouse.y * 20 + 20 - this.camera.position.y) * 0.05;
    }
    this.camera.lookAt(this.scene.position);

    // Deep Topological Fluid Math — skipped entirely under reduced-motion to save GPU/CPU
    if (this.particles && !this.prefersReducedMotion) {
      const positions = this.particles.geometry.attributes.position.array;

      // Calculate a local mouse mapped into the plane's world space for interaction
      const mappedMouseX = this.mouse.x * 200;
      const mappedMouseZ = -this.mouse.y * 200; // mapped to Z because plane is rotated

      for (let i = 0; i < positions.length; i += 3) {
        const x = positions[i];
        const z = positions[i + 2]; // The original Y before rotation

        // 1. Base Math Waves (Simplex-like interference)
        let wave = Math.sin(x * 0.01 + elapsedTime * 0.5) * 15;
        wave += Math.cos(z * 0.01 + elapsedTime * 0.4) * 15;
        wave += Math.sin((x * z) * 0.0001 + elapsedTime * 1.0) * 5;

        // 2. Mouse Repulsion/Ripple (The 'Stealth Luxury' touch)
        const dx = x - mappedMouseX;
        const dz = z - mappedMouseZ;
        const distSq = dx * dx + dz * dz;

        if (distSq < 20000) { // Large interaction radius
          const dist = Math.sqrt(distSq);
          const force = (1 - (dist / 141.4)); // 141.4 is sqrt(20000)

          // Add a beautiful damping ripple originating from mouse
          wave -= Math.sin(dist * 0.1 - elapsedTime * 5) * 15 * force;
        }

        // Apply updated height (Y axis in world space)
        positions[i + 1] = wave;
      }

      this.particles.geometry.attributes.position.needsUpdate = true;
    }

    // Interactive Title Tilt — disabled on touch devices and under reduced-motion
    const titleEl = document.getElementById('main-title');
    if (titleEl && !this.isMobile && !this.isTouch && !this.prefersReducedMotion) {
      titleEl.style.transform = `rotateX(${this.mouse.y * -10}deg) rotateY(${this.mouse.x * 15}deg)`;
    }

    // Free-orbit skills physics
    if (this.skillObjects && this.skillObjects.length > 0 && this.skillsActive) {

      this.raycaster.setFromCamera(this.targetMouse, this.camera);
      const intersects = this.raycaster.intersectObjects(this.skillObjects);
      const hoveredSkill = intersects.length > 0 ? intersects[0].object : null;

      this.skillObjects.forEach(sprite => {
        const ud = sprite.userData;
        const masterAlpha = this.globalSkillOpacity ? this.globalSkillOpacity.value : 0;

        // Apply velocity — frozen under reduced-motion (skills still visible, just static)
        if (!this.prefersReducedMotion) {
          sprite.position.add(ud.velocity);
        }

        // Soft boundary: when too far out, nudge velocity back toward center
        // This creates a gentle curve, not a hard wall — skills CAN go off-screen temporarily
        const softLimit = ud.spawnRange * 1.4;
        if (Math.abs(sprite.position.x) > softLimit) {
          ud.velocity.x -= Math.sign(sprite.position.x) * 0.002;
        }
        if (Math.abs(sprite.position.y - 20) > softLimit * 0.6) {
          ud.velocity.y -= Math.sign(sprite.position.y - 20) * 0.002;
        }
        if (sprite.position.z > -5 || sprite.position.z < -(ud.depthRange * 1.5)) {
          ud.velocity.z -= Math.sign(sprite.position.z + ud.depthRange * 0.5) * 0.001;
        }

        // Depth-based opacity
        const depthFactor = Math.max(0.2, 1 - (Math.abs(sprite.position.z) / (ud.depthRange * 1.2)));
        let targetOpacity = depthFactor * masterAlpha * 0.7;
        let targetScaleLen = ud.baseScale.length();

        const isHovered = (sprite === hoveredSkill);
        if (isHovered) {
          targetOpacity = masterAlpha;
          targetScaleLen = ud.baseScale.length() * 1.3;
        }

        sprite.material.opacity += (targetOpacity - sprite.material.opacity) * 0.06;
        const newScale = ud.baseScale.clone().setLength(targetScaleLen);
        sprite.scale.lerp(newScale, 0.06);
      });
    }

    // Render with bloom composer when available, raw renderer otherwise (mobile, low-end, reduced-motion)
    if (this.composer && this.useBloom) {
      this.composer.render();
    } else {
      this.renderer.render(this.scene, this.camera);
    }
  }
}

// Boot Application
document.addEventListener('DOMContentLoaded', () => {
  window.porlanApp = new PorlanTeaserApp();
});